import { AsyncTask, AsyncTaskState } from 'library/async';
import { useCallback, useState } from 'react';
import { useAppContext } from 'context-providers';
import { QueryPayloadOverride, QueryResponseMetadata } from '@insight2profit/api-connector/services/documentService';

interface UseDocsProps {
  clientId: string;
  documentType: string;
}
export function useDocs<T>({ clientId, documentType }: UseDocsProps = { clientId: '', documentType: '' }) {
  const { insightApi } = useAppContext();
  const [getDocTask, setGetDocTask] = useState<AsyncTask<T>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });
  const [getDocsTask, setGetDocsTask] = useState<AsyncTask<{ data: T[]; metadata: QueryResponseMetadata }>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });
  const [getDocsByTypeTask, setGetDocsByTypeTask] = useState<AsyncTask<T[]>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });

  const [saveDocTask, setSaveDocTask] = useState<AsyncTask<any>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });
  const [updateDocTask, setUpdateDocTask] = useState<AsyncTask<any>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });

  const getDoc = useCallback(
    (id: string) => {
      setGetDocTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });
      return insightApi
        .getDocument<T>(clientId, documentType, id)
        .then(result => {
          setGetDocTask({
            taskState: AsyncTaskState.COMPLETED,
            result,
          });
          return result as T;
        })
        .catch(error => {
          setGetDocTask({
            taskState: AsyncTaskState.ERRORED,
            error,
          });
          throw new Error(error.message);
        });
    },
    [clientId, documentType, insightApi]
  );

  const getDocs = useCallback(
    (query: QueryPayloadOverride) => {
      setGetDocsTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });
      return insightApi
        .getDocumentsByQuery(clientId, documentType, query)
        .then(res => {
          setGetDocsTask({
            taskState: AsyncTaskState.COMPLETED,
            result: { data: res.data as T[], metadata: res.metadata },
          });
          return { data: res.data as T[], metadata: res.metadata };
        })
        .catch(error => {
          setGetDocsTask({
            taskState: AsyncTaskState.ERRORED,
            error,
          });
          throw new Error(error.message);
        });
    },
    [insightApi, clientId, documentType]
  );

  const getDocsByType = useCallback(
    (sortBy?: string, sortOrder?: string, pageSize?: number, page?: number) => {
      setGetDocsByTypeTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });
      return insightApi
        .getDocumentsByType(clientId, documentType, sortBy, sortOrder, pageSize, page)
        .then(res => {
          setGetDocsByTypeTask({
            taskState: AsyncTaskState.COMPLETED,
            result: ([] as unknown) as T[], // for dummy data
          });
          return res as T[];
        })
        .catch(error => {
          setGetDocsByTypeTask({
            taskState: AsyncTaskState.ERRORED,
            error,
          });
          throw new Error(error.message);
        });
    },
    [insightApi, clientId, documentType]
  );

  const saveDoc = useCallback(
    async (body: T): Promise<(T & { id: string }) | void> => {
      if (!body) return;
      setSaveDocTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });
      return await insightApi
        .saveDocument(clientId, documentType, body)
        .then(result => {
          setSaveDocTask({ taskState: AsyncTaskState.COMPLETED, result });
          return result;
        })
        .catch(error => {
          setSaveDocTask({
            taskState: AsyncTaskState.ERRORED,
            error,
          });
          throw new Error(error.message);
        });
    },
    [clientId, documentType, insightApi]
  );

  const resetSaveDocTask = useCallback(() => {
    setSaveDocTask({
      taskState: AsyncTaskState.NOT_STARTED,
    });
  }, [setSaveDocTask]);

  const updateDoc = useCallback(
    (documentId: string, body: T) => {
      setUpdateDocTask({ taskState: AsyncTaskState.IN_PROGRESS });
      return insightApi
        .updateDocument(clientId, documentType, documentId, body)
        .then(result => {
          setUpdateDocTask({
            taskState: AsyncTaskState.COMPLETED,
            result,
          });
          return result;
        })
        .catch(error => {
          setUpdateDocTask({
            taskState: AsyncTaskState.ERRORED,
            error,
          });
        });
    },
    [clientId, documentType, insightApi]
  );

  return {
    getDoc,
    getDocTask,
    getDocs,
    getDocsTask,
    getDocsByType,
    getDocsByTypeTask,
    saveDoc,
    saveDocTask,
    resetSaveDocTask,
    updateDoc,
    updateDocTask,
  };
}
